import { useEffect, useRef, useState } from "react";
import { useAdminCategories, type AdminCategory } from "@/hooks/useAdminCategories";
import AdminHeader from "@/pages/admin/components/AdminHeader";
import ConfirmDialog from "@/pages/admin/components/ConfirmDialog";
import { clearDraft, readDraft, writeDraft } from "@/utils/adminDraft";

const ICON_OPTIONS = [
  "ri-tv-2-line", "ri-smartphone-line", "ri-computer-line", "ri-fridge-line",
  "ri-home-heart-line", "ri-t-shirt-line", "ri-run-line", "ri-car-line",
  "ri-book-line", "ri-music-line", "ri-camera-line", "ri-gamepad-line",
  "ri-tools-line", "ri-leaf-line", "ri-heart-pulse-line", "ri-shopping-bag-3-line",
  "ri-restaurant-line", "ri-plane-line", "ri-gift-line", "ri-paint-brush-line",
  "ri-cpu-line", "ri-headphone-line", "ri-time-line", "ri-bike-line",
];

type ModalMode = "add" | "edit";

type CategoryForm = {
  name: string;
  slug: string;
  icon: string;
  count: number;
  image: string;
  subcategories: string[];
  visible: boolean;
};

type CategoryDraft = {
  form: CategoryForm;
  subcategoryInput: string;
};

const emptyForm: CategoryForm = {
  name: "",
  slug: "",
  icon: "ri-shopping-bag-3-line",
  count: 0,
  image: "",
  subcategories: [],
  visible: true,
};

const CATEGORY_DRAFT_STORAGE_KEY = "afiliabr_admin_category_draft_v1";

function hasMeaningfulCategoryDraft(draft: CategoryDraft) {
  return Boolean(
    draft.form.name.trim() ||
    draft.form.slug.trim() ||
    draft.form.image.trim() ||
    draft.form.count > 0 ||
    draft.form.subcategories.length > 0 ||
    draft.subcategoryInput.trim() ||
    draft.form.icon !== emptyForm.icon ||
    draft.form.visible !== emptyForm.visible
  );
}

function readStoredCategoryDraft() {
  return readDraft<CategoryDraft>(CATEGORY_DRAFT_STORAGE_KEY);
}

function hasStoredCategoryDraft() {
  const draft = readStoredCategoryDraft();
  return Boolean(draft && hasMeaningfulCategoryDraft(draft));
}

function clearStoredCategoryDraft() {
  clearDraft(CATEGORY_DRAFT_STORAGE_KEY);
}

export default function AdminCategories() {
  const {
    categories,
    addCategory,
    updateCategory,
    deleteCategory,
    reorder,
    toggleVisible,
    resetToDefault,
    slugify,
  } = useAdminCategories();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CategoryForm>(readStoredCategoryDraft()?.form ?? emptyForm);
  const [subcategoryInput, setSubcategoryInput] = useState(readStoredCategoryDraft()?.subcategoryInput ?? "");
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [draftRestoredOnPageLoad, setDraftRestoredOnPageLoad] = useState(false);

  // Drag state
  const dragIndex = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const normalizeSubcategories = (subcategories: string[]) =>
    Array.from(new Set(subcategories.map((subcategory) => subcategory.trim()).filter(Boolean)));

  useEffect(() => {
    if (draftRestoredOnPageLoad || modalOpen) return;
    if (!hasStoredCategoryDraft()) return;

    const draft = readStoredCategoryDraft();
    if (!draft) return;

    setModalMode("add");
    setEditingId(null);
    setForm(draft.form);
    setSubcategoryInput(draft.subcategoryInput);
    setModalOpen(true);
    setDraftRestoredOnPageLoad(true);
    showToast("Rascunho de categoria restaurado automaticamente.");
  }, [draftRestoredOnPageLoad, modalOpen]);

  useEffect(() => {
    if (modalMode !== "add") return;

    writeDraft(
      CATEGORY_DRAFT_STORAGE_KEY,
      { form, subcategoryInput },
      hasMeaningfulCategoryDraft
    );
  }, [form, modalMode, subcategoryInput]);

  const openAdd = () => {
    const draft = readStoredCategoryDraft();
    setEditingId(null);
    setModalMode("add");
    setForm(draft?.form ?? { ...emptyForm });
    setSubcategoryInput(draft?.subcategoryInput ?? "");
    setModalOpen(true);
    setIconPickerOpen(false);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setIconPickerOpen(false);
  };

  const openEdit = (cat: AdminCategory) => {
    setForm({
      name: cat.name,
      slug: cat.slug,
      icon: cat.icon,
      count: cat.count,
      image: cat.image,
      subcategories: [...cat.subcategories],
      visible: cat.visible,
    });
    setSubcategoryInput("");
    setModalMode("edit");
    setEditingId(cat.id);
    setModalOpen(true);
    setIconPickerOpen(false);
  };

  const handleFormChange = (field: keyof CategoryForm, value: string | number | boolean | string[]) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "name" && typeof value === "string") {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleAddSubcategory = () => {
    const nextSubcategory = subcategoryInput.trim();
    if (!nextSubcategory) return;

    handleFormChange("subcategories", normalizeSubcategories([...form.subcategories, nextSubcategory]));
    setSubcategoryInput("");
  };

  const handleRemoveSubcategory = (subcategoryToRemove: string) => {
    handleFormChange(
      "subcategories",
      form.subcategories.filter((subcategory) => subcategory !== subcategoryToRemove)
    );
  };

  const handleSave = () => {
    if (!form.name.trim()) return;
    const subcategories = normalizeSubcategories(form.subcategories);

    if (modalMode === "add") {
      addCategory({
        name: form.name,
        slug: form.slug || slugify(form.name),
        icon: form.icon,
        count: form.count,
        image: form.image,
        subcategories,
        visible: form.visible,
      });
      clearStoredCategoryDraft();
      setForm({ ...emptyForm });
      setSubcategoryInput("");
      showToast("Categoria criada com sucesso!");
    } else if (editingId !== null) {
      updateCategory(editingId, {
        name: form.name,
        slug: form.slug || slugify(form.name),
        icon: form.icon,
        count: form.count,
        image: form.image,
        subcategories,
        visible: form.visible,
      });
      showToast("Categoria atualizada!");
    }
    setModalOpen(false);
  };

  const handleDelete = (id: number) => {
    deleteCategory(id);
    setConfirmId(null);
    showToast("Categoria removida.");
  };

  // Drag handlers
  const handleDragStart = (index: number) => {
    dragIndex.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOver(index);
  };

  const handleDrop = (toIndex: number) => {
    if (dragIndex.current !== null && dragIndex.current !== toIndex) {
      reorder(dragIndex.current, toIndex);
    }
    dragIndex.current = null;
    setDragOver(null);
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
    setDragOver(null);
  };

  return (
    <div>
      <AdminHeader
        title="Categorias"
        subtitle="Gerencie e reordene as categorias do menu de navegação"
        action={
          <div className="flex gap-3">
            <button
              onClick={resetToDefault}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-1.5"></i>
              Restaurar Padrão
            </button>
            <button
              onClick={openAdd}
              className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-1.5"></i>
              Nova Categoria
            </button>
          </div>
        }
      />

      {/* Info Banner */}
      <div className="mb-5 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 flex items-center gap-3">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <i className="ri-drag-move-2-line text-amber-500"></i>
        </div>
        <p className="text-sm text-amber-700">
          <strong>Arraste e solte</strong> as linhas para reordenar as categorias no menu de navegação. Categorias ocultas não aparecem no site.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-50 text-orange-600">
            <i className="ri-apps-2-line text-lg"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
            <p className="text-xs text-gray-500">Total de categorias</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <i className="ri-eye-line text-lg"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{categories.filter((c) => c.visible).length}</p>
            <p className="text-xs text-gray-500">Visíveis no menu</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-500">
            <i className="ri-eye-off-line text-lg"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{categories.filter((c) => !c.visible).length}</p>
            <p className="text-xs text-gray-500">Ocultas</p>
          </div>
        </div>
      </div>

      {/* Categories List */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="w-10 px-4 py-3.5"></th>
              <th className="text-left px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Categoria</th>
              <th className="text-left px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Slug</th>
              <th className="text-left px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Subcategorias</th>
              <th className="text-center px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Produtos</th>
              <th className="text-center px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Visível</th>
              <th className="text-right px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ações</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat, index) => (
              <tr
                key={cat.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={() => handleDrop(index)}
                onDragEnd={handleDragEnd}
                className={`border-b border-gray-50 transition-all ${
                  dragOver === index ? "bg-orange-50 border-orange-200" : "hover:bg-gray-50/50"
                } ${!cat.visible ? "opacity-50" : ""}`}
              >
                <td className="px-4 py-3.5">
                  <div className="w-5 h-5 flex items-center justify-center text-gray-300 cursor-grab active:cursor-grabbing">
                    <i className="ri-drag-move-2-line text-base"></i>
                  </div>
                </td>

                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-orange-50 flex-shrink-0">
                      <i className={`${cat.icon} text-orange-500 text-base`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{cat.name}</p>
                      <p className="text-xs text-gray-400">{cat.subcategories.length} subcategorias</p>
                    </div>
                  </div>
                </td>

                <td className="px-4 py-3.5">
                  <code className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md">/categoria/{cat.slug}</code>
                </td>

                <td className="px-4 py-3.5">
                  <div className="flex flex-wrap gap-1 max-w-xs">
                    {cat.subcategories.slice(0, 3).map((sub) => (
                      <span key={sub} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full whitespace-nowrap">
                        {sub}
                      </span>
                    ))}
                    {cat.subcategories.length > 3 && (
                      <span className="text-xs text-gray-400">+{cat.subcategories.length - 3}</span>
                    )}
                  </div>
                </td>

                <td className="px-4 py-3.5 text-center">
                  <span className="text-sm font-semibold text-gray-700">{cat.count.toLocaleString("pt-BR")}</span>
                </td>

                <td className="px-4 py-3.5 text-center">
                  <button
                    onClick={() => toggleVisible(cat.id)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
                      cat.visible ? "bg-orange-500" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        cat.visible ? "translate-x-4" : "translate-x-1"
                      }`}
                    />
                  </button>
                </td>

                <td className="px-4 py-3.5 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => openEdit(cat)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-orange-50 hover:text-orange-500 hover:border-orange-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setConfirmId(cat.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {modalMode === "add" ? "Nova Categoria" : "Editar Categoria"}
                </h2>
                {modalMode === "add" && hasStoredCategoryDraft() && (
                  <p className="text-xs text-orange-600 mt-1">Rascunho restaurado automaticamente para continuar o cadastro.</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {modalMode === "add" && hasStoredCategoryDraft() && (
                  <button
                    type="button"
                    onClick={() => {
                      clearStoredCategoryDraft();
                      setForm({ ...emptyForm });
                      setSubcategoryInput("");
                    }}
                    className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                  >
                    Limpar rascunho
                  </button>
                )}
                <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            </div>

            <div className="px-6 py-5 space-y-4">
              {modalMode === "add" && (
                <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                  O cadastro salva seu progresso automaticamente enquanto você preenche. Você pode sair da tela e voltar depois sem perder o rascunho.
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nome da Categoria <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  placeholder="Ex: Eletrônicos"
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Slug (URL)</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400 whitespace-nowrap">/categoria/</span>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => handleFormChange("slug", e.target.value)}
                    placeholder="eletronicos"
                    className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-mono focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Ícone</label>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-50 flex-shrink-0">
                    <i className={`${form.icon} text-orange-500 text-xl`}></i>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIconPickerOpen(!iconPickerOpen)}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {iconPickerOpen ? "Fechar seletor" : "Escolher ícone"}
                  </button>
                </div>
                {iconPickerOpen && (
                  <div className="mt-3 grid grid-cols-8 gap-2 p-3 rounded-xl border border-gray-100 bg-gray-50">
                    {ICON_OPTIONS.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => { handleFormChange("icon", icon); setIconPickerOpen(false); }}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all cursor-pointer ${
                          form.icon === icon
                            ? "bg-orange-500 text-white"
                            : "bg-white hover:bg-orange-50 hover:text-orange-500 text-gray-600 border border-gray-100"
                        }`}
                        title={icon}
                      >
                        <i className={`${icon} text-base`}></i>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Quantidade de Produtos</label>
                <input
                  type="number"
                  value={form.count}
                  onChange={(e) => handleFormChange("count", parseInt(e.target.value) || 0)}
                  min={0}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">URL da Imagem</label>
                <input
                  type="url"
                  value={form.image}
                  onChange={(e) => handleFormChange("image", e.target.value)}
                  placeholder="https://..."
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Subcategorias</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={subcategoryInput}
                    onChange={(e) => setSubcategoryInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddSubcategory();
                      }
                    }}
                    placeholder="Ex: Smartphones"
                    className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={handleAddSubcategory}
                    className="px-4 py-2 rounded-lg bg-gray-900 hover:bg-black text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Adicionar
                  </button>
                </div>
                <div className="mt-3 min-h-14 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-3">
                  {form.subcategories.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {form.subcategories.map((subcategory) => (
                        <span
                          key={subcategory}
                          className="inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 px-3 py-1 text-sm text-gray-700"
                        >
                          {subcategory}
                          <button
                            type="button"
                            onClick={() => handleRemoveSubcategory(subcategory)}
                            className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                            aria-label={`Remover ${subcategory}`}
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">Cadastre as subcategorias individualmente dentro desta categoria.</p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Visível no menu</p>
                  <p className="text-xs text-gray-400">Exibir esta categoria no menu de navegação</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleFormChange("visible", !form.visible)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                    form.visible ? "bg-orange-500" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      form.visible ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={!form.name.trim()}
                className="px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                {modalMode === "add" ? "Criar Categoria" : "Salvar Alterações"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmId !== null && (
        <ConfirmDialog
          title="Excluir categoria?"
          message="Esta ação não pode ser desfeita. A categoria será removida do menu de navegação."
          onConfirm={() => handleDelete(confirmId)}
          onCancel={() => setConfirmId(null)}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-xl z-50 flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-green-400"></i>
          {toast}
        </div>
      )}
    </div>
  );
}